import { type ReactNode } from 'react'

import { Footer } from '../components/Footer'
import { Navbar } from '../components/Navbar'
import { ORG, type Block, type LegalDocument } from '../content/legal'

/**
 * The shared renderer for the three legal documents (M17-T17).
 *
 * One component rather than three hand-written pages: the documents are
 * approved as text, so the only thing a page should decide is typography. Any
 * per-page markup would be an invitation to reword one of them in the component
 * layer, which is the one thing `docs/legal/README.md` forbids.
 *
 * Long-form reading, so the measure is capped and the body runs at 16/26 rather
 * than the 16/24 the rest of the app uses — these pages are read in paragraphs,
 * not scanned.
 *
 * A NULL CONTACT LINE IS OMITTED, NOT PRINTED. `ORG` starts out all-null and a
 * page that rendered "Email: [Insert email]" would look finished while being
 * unusable. See `legalContactComplete()`.
 */
export function LegalPage({ doc }: { doc: LegalDocument }): ReactNode {
  return (
    <main className="flex min-h-screen flex-col bg-bg-app font-sans text-ink-1">
      <Navbar />

      <article className="mx-auto w-full max-w-2xl px-5 py-10 sm:px-8">
        <h1 className="font-display text-d-sm font-medium tracking-display text-ink-1 sm:text-d-md">
          {doc.title}
        </h1>
        <p className="mt-3 text-[18px] leading-7 text-ink-2">
          {doc.standfirst}
        </p>

        {doc.dated && ORG.effectiveDate !== null && (
          <p className="mt-4 text-[14px] leading-5 text-ink-3">
            Effective date: {ORG.effectiveDate} · Last updated:{' '}
            {ORG.effectiveDate}
          </p>
        )}

        <div className="mt-8 flex flex-col gap-4">
          {doc.intro.map((b, i) => (
            <BlockView key={i} block={b} />
          ))}
        </div>

        {doc.sections.map((section) => (
          <section key={section.heading} className="mt-10">
            <h2 className="text-[20px] font-medium leading-7 text-ink-1">
              {section.heading}
            </h2>
            <div className="mt-4 flex flex-col gap-4">
              {section.blocks.map((b, i) => (
                <BlockView key={i} block={b} />
              ))}
            </div>
          </section>
        ))}
      </article>

      <Footer />
    </main>
  )
}

function BlockView({ block }: { block: Block }): ReactNode {
  if (block.kind === 'p') {
    return <p className="text-[16px] leading-[26px] text-ink-2">{block.text}</p>
  }

  if (block.kind === 'ul') {
    return (
      <ul className="flex list-disc flex-col gap-2 pl-6 text-[16px] leading-[26px] text-ink-2 marker:text-ink-4">
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    )
  }

  const lines = block.lines.filter((l) => l.value !== null)

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[16px] leading-[26px] text-ink-2">{block.intro}</p>
      {lines.length > 0 && (
        <address className="rounded-r3 border border-line-1 bg-bg-soft px-5 py-4 text-[16px] leading-[26px] not-italic text-ink-1">
          {lines.map((l) => (
            <span key={l.label ?? 'name'} className="block">
              {l.label === null ? (
                <span className="font-medium">{l.value}</span>
              ) : (
                <>
                  {l.label}: <ContactValue value={l.value ?? ''} />
                </>
              )}
            </span>
          ))}
        </address>
      )}
      {block.outro?.map((text) => (
        <p key={text} className="text-[16px] leading-[26px] text-ink-2">
          {text}
        </p>
      ))}
    </div>
  )
}

/** Emails and phone numbers are the whole point of a contact block — they are
 *  actionable here rather than text to copy out by hand. */
function ContactValue({ value }: { value: string }): ReactNode {
  const href = value.includes('@')
    ? `mailto:${value}`
    : /^\+?[\d\s-]{7,}$/.test(value)
      ? `tel:${value.replace(/\s/g, '')}`
      : null

  return href === null ? (
    <>{value}</>
  ) : (
    <a
      href={href}
      className="text-accent-ink underline hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      {value}
    </a>
  )
}
